/**
 * Permission catalog — NOT the source of truth for anything the platform enforces.
 *
 * # What this file is NOT, as of S-395
 *
 * Its header used to read "the single source of truth for RBAC … intended for
 * backend enforcement once a server exists". The server exists and it enforces
 * something else: six RELATIONS the authorization model defines
 * (`lib/rbac/capabilities.ts`, mirroring `authz.ProjectCapabilities` and the
 * OpenAPI's closed `RoleGrant.capability` enum). **None of the `resource:action`
 * strings below is one of them**, and eleven of these resources have no project
 * relation at all.
 *
 * **These strings no longer reach any surface that grants authority.** The Roles
 * editor offered them as checkboxes until S-395; once the page read the platform
 * over HTTP that grid became actively dishonest, because `POST /v1/roles` takes a
 * name and a description and discarded every box an administrator ticked. The
 * grid is gone and capabilities are granted from the role's card.
 *
 * # What it still does, and why it was not deleted
 *
 * `type Permission` gates NAV, the command palette and `<Can>`. In MOCK mode the
 * demo store carries a role's powers inline. Over HTTP, since 2.795 (SPONSOR
 * RULING 2026-10-02, relayed by the coordinator), each name is DECIDED from the
 * platform's own answer about the caller (S-709, ADR-106) through
 * `platform-gate.ts`, which maps it to the relation the gateway requires; a name
 * with no counterpart there is never hidden. When that read fails, gating is
 * fail-open as before (`use-permission.ts`).
 *
 * Do not add to this list. A new name must be mapped in `platform-gate.ts` to a
 * relation the model defines, or it stays shown for everyone.
 */

export const PERMISSION_GROUPS = [
  {
    resource: "targets",
    label: "Targets",
    actions: ["read", "create", "update", "delete", "scan"],
  },
  {
    resource: "scans",
    label: "Scans",
    actions: ["read", "run", "stop", "compare", "delete"],
  },
  {
    resource: "vulnerabilities",
    label: "Vulnerabilities",
    actions: ["read", "mark", "retest"],
  },
  {
    // FR-87 level 3: a project user's whole authority over CAP-011 is the
    // toggle. There is deliberately no "run" — AI testing runs as part of a
    // scan under the AI Penetration Test profile, never as a standalone job.
    resource: "ai-testing",
    label: "AI Penetration Testing",
    actions: ["toggle"],
  },
  {
    // FR-51's lifecycle. `transition` and not `manage`: the only write this
    // surface has is moving an issue along §7.2, and `verified` is not among
    // its targets at any permission level (ARC-039, invariant 5).
    resource: "issues",
    label: "Issues",
    actions: ["read", "transition"],
  },
  {
    // FR-09. `manage` covers mint, rotate and revoke; a key is shown ONCE at
    // mint and never again (DAT-026 stores only an Argon2id hash).
    resource: "api-keys",
    label: "API Keys",
    actions: ["read", "manage"],
  },
  {
    // FR-23 — IFC-014's schedules.
    resource: "schedules",
    label: "Schedules",
    actions: ["read", "manage"],
  },
  {
    // FR-67 / FR-69 — IFC-018's subscriptions, deliveries and alert rules.
    resource: "alerts",
    label: "Alerts & Webhooks",
    actions: ["read", "manage"],
  },
  {
    resource: "reports",
    label: "Reports",
    actions: ["read", "generate", "delete"],
  },
  {
    resource: "team",
    label: "Team / Users",
    actions: ["read", "manage"],
  },
  {
    resource: "roles",
    label: "Roles",
    actions: ["read", "manage"],
  },
  {
    resource: "projects",
    label: "Projects",
    actions: ["read", "manage"],
  },
  {
    resource: "scan-profiles",
    label: "Scan Profiles",
    actions: ["manage"],
  },
  {
    resource: "settings",
    label: "Settings",
    actions: ["manage"],
  },
] as const;

type Group = (typeof PERMISSION_GROUPS)[number];
export type Permission = `${Group["resource"]}:${Group["actions"][number]}`;

/** Flat list of every permission string. */
export const ALL_PERMISSIONS: Permission[] = PERMISSION_GROUPS.flatMap((g) =>
  g.actions.map((a) => `${g.resource}:${a}` as Permission),
);

export function isPermission(value: string): value is Permission {
  return (ALL_PERMISSIONS as string[]).includes(value);
}
